import { c as _c } from "react/compiler-runtime";
import { IconBook2, IconBubbleText, IconClearAll } from "@tabler/icons-react";
import { ChevronsUpDown, LogOut, Settings, ShieldCheck, User } from "lucide-react";
import React, { useCallback, useMemo } from "react";
import { useTranslations } from "next-intl";
import { useRouter } from "next/navigation";
import { Avatar, AvatarFallback, AvatarImage } from "@components/ui/avatar";
import { DropdownMenu, DropdownMenuContent, DropdownMenuGroup, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger } from "@components/ui/dropdown-menu";
import { SidebarMenuButton, useSidebar } from "@components/ui/sidebar";
import { environment } from "@lib/environment";
import { useAnyTranslations } from "@lib/i18n/utils";
import { useAppDispatch, useAppSelector } from "@lib/redux/hooks";
import { logout } from "@features/application/app-slice";
import { resolveAuthLogout } from "@features/auth/auth-logout";
import { AccessToken } from "@features/auth/types/access-token";
import { UserTokenValue } from "@features/auth/types/access-token";
import routes from "@features/routes";
import { selectUserInformationPanelHides } from "@features/settings/settings-selectors";
import { clearInformationPanelHides } from "@features/settings/settings-slice";
interface NavUserInfoProps {
  user: UserTokenValue;
  isPrivateMode?: boolean;
}
export const NavUserInfo = t0 => {
  const $ = _c(100);
  const {
    user,
    isPrivateMode: t1
  } = t0;
  const isPrivateMode = t1 === undefined ? false : t1;
  const {
    isMobile
  } = useSidebar();
  const router = useRouter();
  const dispatch = useAppDispatch();
  const t = useTranslations("features.auth.ui.nav-user");
  const tSettings = useTranslations("common.settings");
  const tAny = useAnyTranslations();
  const {
    sub,
    hides
  } = useAppSelector(selectUserInformationPanelHides);
  let t2;
  if ($[0] !== user.displayName) {
    t2 = user.displayName?.split(" ").map(_temp).join("").toUpperCase().substring(0, 2) ?? "CN";
    $[0] = user.displayName;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const initials = t2;
  let t3;
  if ($[2] !== dispatch || $[3] !== sub) {
    t3 = () => {
      if (sub) {
        dispatch(clearInformationPanelHides({
          sub
        }));
      }
    };
    $[2] = dispatch;
    $[3] = sub;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  const onClearInformationPanelHidesClick = t3;
  let t4;
  if ($[5] !== initials || $[6] !== user.picture || $[7] !== user.user_name) {
    t4 = <Avatar className="h-8 w-8 rounded-md">{user.picture ? <AvatarImage src={user.picture} alt={user.user_name ?? "User"} /> : <AvatarFallback className="rounded-md">{initials}</AvatarFallback>}</Avatar>;
    $[5] = initials;
    $[6] = user.picture;
    $[7] = user.user_name;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  const UserAvatar = t4;
  let t5;
  if ($[9] !== dispatch || $[10] !== router) {
    t5 = () => {
      resolveAuthLogout().finally(() => {
        dispatch(logout());
        router.refresh();
      });
    };
    $[9] = dispatch;
    $[10] = router;
    $[11] = t5;
  } else {
    t5 = $[11];
  }
  const logoutClickHandler = t5;
  const t6 = !user;
  const t7 = user.displayName ?? "User";
  let t8;
  if ($[12] !== t7) {
    t8 = <span className="truncate font-medium">{t7}</span>;
    $[12] = t7;
    $[13] = t8;
  } else {
    t8 = $[13];
  }
  const t9 = user.email ?? "";
  let t10;
  if ($[14] !== t9) {
    t10 = <span className="truncate text-xs">{t9}</span>;
    $[14] = t9;
    $[15] = t10;
  } else {
    t10 = $[15];
  }
  let t11;
  if ($[16] !== t10 || $[17] !== t8) {
    t11 = <div className="grid flex-1 text-left text-sm leading-tight">{t8}{t10}</div>;
    $[16] = t10;
    $[17] = t8;
    $[18] = t11;
  } else {
    t11 = $[18];
  }
  let t12;
  if ($[19] === Symbol.for("react.memo_cache_sentinel")) {
    t12 = <ChevronsUpDown className="ml-auto size-4" />;
    $[19] = t12;
  } else {
    t12 = $[19];
  }
  let t13;
  if ($[20] !== UserAvatar || $[21] !== t11) {
    t13 = <SidebarMenuButton size="lg" className="data-[state=open]:bg-sidebar-accent data-[state=open]:text-sidebar-accent-foreground">{UserAvatar}{t11}{t12}</SidebarMenuButton>;
    $[20] = UserAvatar;
    $[21] = t11;
    $[22] = t13;
  } else {
    t13 = $[22];
  }
  let t14;
  if ($[23] !== t13 || $[24] !== t6) {
    t14 = <DropdownMenuTrigger asChild={true} disabled={t6}>{t13}</DropdownMenuTrigger>;
    $[23] = t13;
    $[24] = t6;
    $[25] = t14;
  } else {
    t14 = $[25];
  }
  const t15 = isMobile ? "bottom" : "right";
  const t16 = user?.displayName ?? "User";
  let t17;
  if ($[26] !== t16) {
    t17 = <span className="truncate font-medium">{t16}</span>;
    $[26] = t16;
    $[27] = t17;
  } else {
    t17 = $[27];
  }
  const t18 = user?.email ?? "";
  let t19;
  if ($[28] !== t18) {
    t19 = <span className="truncate text-xs">{t18}</span>;
    $[28] = t18;
    $[29] = t19;
  } else {
    t19 = $[29];
  }
  let t20;
  if ($[30] !== t17 || $[31] !== t19) {
    t20 = <div className="grid flex-1 text-left text-sm leading-tight">{t17}{t19}</div>;
    $[30] = t17;
    $[31] = t19;
    $[32] = t20;
  } else {
    t20 = $[32];
  }
  let t21;
  if ($[33] !== UserAvatar || $[34] !== t20) {
    t21 = <DropdownMenuLabel className="p-0 font-normal"><div className="flex items-center gap-2 px-1 py-1.5 text-left text-sm">{UserAvatar}{t20}</div></DropdownMenuLabel>;
    $[33] = UserAvatar;
    $[34] = t20;
    $[35] = t21;
  } else {
    t21 = $[35];
  }
  let t22;
  if ($[36] === Symbol.for("react.memo_cache_sentinel")) {
    t22 = <DropdownMenuSeparator />;
    $[36] = t22;
  } else {
    t22 = $[36];
  }
  let t23;
  if ($[37] !== router) {
    t23 = () => {
      router.push(environment.urls.external.lkApiUrl);
    };
    $[37] = router;
    $[38] = t23;
  } else {
    t23 = $[38];
  }
  let t24;
  if ($[39] === Symbol.for("react.memo_cache_sentinel")) {
    t24 = <User />;
    $[39] = t24;
  } else {
    t24 = $[39];
  }
  let t25;
  if ($[40] !== t) {
    t25 = t("profile");
    $[40] = t;
    $[41] = t25;
  } else {
    t25 = $[41];
  }
  let t26;
  if ($[42] !== t23 || $[43] !== t25) {
    t26 = <DropdownMenuItem onClick={t23}>{t24}{t25}</DropdownMenuItem>;
    $[42] = t23;
    $[43] = t25;
    $[44] = t26;
  } else {
    t26 = $[44];
  }
  let t27;
  if ($[45] !== router) {
    t27 = () => {
      router.push(routes.personal_access_tokens.pages.owner.path());
    };
    $[45] = router;
    $[46] = t27;
  } else {
    t27 = $[46];
  }
  let t28;
  if ($[47] === Symbol.for("react.memo_cache_sentinel")) {
    t28 = <Settings />;
    $[47] = t28;
  } else {
    t28 = $[47];
  }
  let t29;
  if ($[48] !== t) {
    t29 = t("settings");
    $[48] = t;
    $[49] = t29;
  } else {
    t29 = $[49];
  }
  let t30;
  if ($[50] !== t27 || $[51] !== t29) {
    t30 = <DropdownMenuItem onClick={t27}>{t28}{t29}</DropdownMenuItem>;
    $[50] = t27;
    $[51] = t29;
    $[52] = t30;
  } else {
    t30 = $[52];
  }
  let t31;
  if ($[53] !== isPrivateMode || $[54] !== router || $[55] !== t || $[56] !== user) {
    t31 = isPrivateMode && AccessToken.isGlobalAdmin(user) && <DropdownMenuItem onClick={() => {
      router.push(routes.personal_access_tokens.pages.admin.path());
    }}><ShieldCheck />{t("administration")}</DropdownMenuItem>;
    $[53] = isPrivateMode;
    $[54] = router;
    $[55] = t;
    $[56] = user;
    $[57] = t31;
  } else {
    t31 = $[57];
  }
  let t32;
  if ($[58] !== router) {
    t32 = () => {
      router.push(routes.proposals.pages.speaker_area_proposals.path());
    };
    $[58] = router;
    $[59] = t32;
  } else {
    t32 = $[59];
  }
  let t33;
  if ($[60] === Symbol.for("react.memo_cache_sentinel")) {
    t33 = <IconBubbleText />;
    $[60] = t33;
  } else {
    t33 = $[60];
  }
  let t34;
  if ($[61] !== tAny) {
    t34 = tAny(routes.proposals.pages.speaker_area_proposals.localizationKeys.title);
    $[61] = tAny;
    $[62] = t34;
  } else {
    t34 = $[62];
  }
  let t35;
  if ($[63] !== t32 || $[64] !== t34) {
    t35 = <DropdownMenuItem onClick={t32}>{t33}{t34}</DropdownMenuItem>;
    $[63] = t32;
    $[64] = t34;
    $[65] = t35;
  } else {
    t35 = $[65];
  }
  let t36;
  if ($[66] !== router) {
    t36 = () => {
      router.push(routes.documents_system.pages.home.path());
    };
    $[66] = router;
    $[67] = t36;
  } else {
    t36 = $[67];
  }
  let t37;
  if ($[68] === Symbol.for("react.memo_cache_sentinel")) {
    t37 = <IconBook2 />;
    $[68] = t37;
  } else {
    t37 = $[68];
  }
  let t38;
  if ($[69] !== t) {
    t38 = t("documents");
    $[69] = t;
    $[70] = t38;
  } else {
    t38 = $[70];
  }
  let t39;
  if ($[71] !== t36 || $[72] !== t38) {
    t39 = <DropdownMenuItem onClick={t36}>{t37}{t38}</DropdownMenuItem>;
    $[71] = t36;
    $[72] = t38;
    $[73] = t39;
  } else {
    t39 = $[73];
  }
  let t40;
  if ($[74] !== t26 || $[75] !== t30 || $[76] !== t31 || $[77] !== t35 || $[78] !== t39) {
    t40 = <DropdownMenuGroup>{t26}{t30}{t31}{t35}{t39}</DropdownMenuGroup>;
    $[74] = t26;
    $[75] = t30;
    $[76] = t31;
    $[77] = t35;
    $[78] = t39;
    $[79] = t40;
  } else {
    t40 = $[79];
  }
  let t41;
  if ($[80] !== hides || $[81] !== onClearInformationPanelHidesClick || $[82] !== tSettings) {
    t41 = hides && <><DropdownMenuSeparator /><DropdownMenuGroup><DropdownMenuItem onClick={onClearInformationPanelHidesClick}><IconClearAll />{tSettings("informationPanelsHides.clearInformationPanels")}</DropdownMenuItem></DropdownMenuGroup></>;
    $[80] = hides;
    $[81] = onClearInformationPanelHidesClick;
    $[82] = tSettings;
    $[83] = t41;
  } else {
    t41 = $[83];
  }
  let t42;
  if ($[84] === Symbol.for("react.memo_cache_sentinel")) {
    t42 = <DropdownMenuSeparator />;
    $[84] = t42;
  } else {
    t42 = $[84];
  }
  let t43;
  if ($[85] === Symbol.for("react.memo_cache_sentinel")) {
    t43 = <LogOut />;
    $[85] = t43;
  } else {
    t43 = $[85];
  }
  let t44;
  if ($[86] !== t) {
    t44 = t("logout");
    $[86] = t;
    $[87] = t44;
  } else {
    t44 = $[87];
  }
  let t45;
  if ($[88] !== logoutClickHandler || $[89] !== t44) {
    t45 = <DropdownMenuItem onClick={logoutClickHandler}>{t43}{t44}</DropdownMenuItem>;
    $[88] = logoutClickHandler;
    $[89] = t44;
    $[90] = t45;
  } else {
    t45 = $[90];
  }
  let t46;
  if ($[91] !== t15 || $[92] !== t21 || $[93] !== t40 || $[94] !== t41 || $[95] !== t45) {
    t46 = <DropdownMenuContent className="w-(--radix-dropdown-menu-trigger-width) min-w-56 rounded-md" side={t15} align="end" sideOffset={4}>{t21}{t22}{t40}{t41}{t42}{t45}</DropdownMenuContent>;
    $[91] = t15;
    $[92] = t21;
    $[93] = t40;
    $[94] = t41;
    $[95] = t45;
    $[96] = t46;
  } else {
    t46 = $[96];
  }
  let t47;
  if ($[97] !== t14 || $[98] !== t46) {
    t47 = <DropdownMenu>{t14}{t46}</DropdownMenu>;
    $[97] = t14;
    $[98] = t46;
    $[99] = t47;
  } else {
    t47 = $[99];
  }
  return t47;
};
function _temp(n) {
  return n[0];
}